import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';

const TermsOfService = () => {
  const navigate = useNavigate();

  // Add noindex meta tag
  useEffect(() => {
    const metaTag = document.createElement('meta');
    metaTag.name = 'robots';
    metaTag.content = 'noindex, nofollow';
    document.head.appendChild(metaTag);

    // Update page title
    document.title = 'Terms of Service | Virtual Red Air';

    return () => {
      document.head.removeChild(metaTag);
      document.title = 'Virtual Red Air | Synthetic Training Ecosystems';
    };
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-sm border-b border-red-900/30">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate('/')} className="text-2xl font-bold">
              <span className="text-white">Virtual</span>
              <span className="text-red-600"> Red Air</span>
            </button>
            <button onClick={() => navigate('/')} className="hover:text-red-500 transition-colors">
              ← Back to Home
            </button>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-24 pb-16 px-6">
        <div className="container mx-auto max-w-4xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-8">
            <span className="text-white">Terms of</span> <span className="text-red-500">Service</span>
          </h1>

          <div className="space-y-8 text-gray-300 leading-relaxed">
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Introduction</h2>
              <p>
                Welcome to Virtual Red Air PC ("Company," "we," "us," or "our"). These Terms of Service ("Terms") govern your access to and use of our website virtualredair.com ("Website") and the services we provide.
              </p>
              <p className="mt-4">
                By accessing or using our Website, you agree to be bound by these Terms. If you do not agree with any part of these Terms, you must not use our Website.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Company Information</h2>
              <p><strong>Company Name:</strong> Virtual Red Air PC</p>
              <p><strong>Location:</strong> Patras, Greece</p>
              <p><strong>Email:</strong> <a href="mailto:info@virtualredair.com" className="text-red-400 hover:text-red-300">info@virtualredair.com</a></p>
              <p><strong>VAT Number:</strong> [placeholder]</p>
              <p><strong>GEMI Number:</strong> [placeholder]</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Use of the Website</h2>
              <p>
                You agree to use our Website only for lawful purposes and in accordance with these Terms. You agree not to:
              </p>
              <ul className="list-disc list-inside ml-4 mt-2 space-y-2">
                <li>Use the Website in any way that violates applicable laws or regulations</li>
                <li>Attempt to gain unauthorized access to any portion of the Website</li>
                <li>Interfere with or disrupt the Website or servers/networks connected to it</li>
                <li>Transmit any viruses, malware, or other malicious code</li>
                <li>Use automated systems (bots, scrapers) without prior written permission</li>
                <li>Collect or harvest personal information from other users</li>
                <li>Impersonate any person or entity</li>
                <li>Use the Website for any commercial purpose without authorization</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Intellectual Property</h2>
              <p>
                All content on this Website, including but not limited to text, graphics, logos, images, videos, and software, is the property of Virtual Red Air PC or its licensors and is protected by Greek, European Union, and international copyright, trademark, and other intellectual property laws.
              </p>
              <p className="mt-4">
                You may not reproduce, distribute, modify, create derivative works, publicly display, or exploit any content from this Website without our prior written consent, except as permitted by law or these Terms.
              </p>
              <p className="mt-4">
                <strong>Permitted Use:</strong> You may view and print pages from the Website for personal, non-commercial use, provided you retain all copyright and proprietary notices.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Website Availability</h2>
              <p>
                We strive to ensure that our Website is available 24/7. However, we do not guarantee uninterrupted access and reserve the right to:
              </p>
              <ul className="list-disc list-inside ml-4 mt-2 space-y-2">
                <li>Suspend or withdraw the Website temporarily or permanently</li>
                <li>Perform maintenance, updates, or modifications</li>
                <li>Restrict access to certain features or sections</li>
              </ul>
              <p className="mt-4">
                We are not liable for any inconvenience or loss resulting from Website unavailability.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">External Links</h2>
              <p>
                Our Website may contain links to third-party websites or resources. These links are provided for your convenience only. We have no control over the content, privacy policies, or practices of third-party websites and accept no responsibility for them.
              </p>
              <p className="mt-4">
                Your use of third-party websites is at your own risk, and we encourage you to review their terms and privacy policies.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Limitation of Liability</h2>
              <p>
                To the fullest extent permitted by law, Virtual Red Air PC and its directors, officers, employees, and agents shall not be liable for any:
              </p>
              <ul className="list-disc list-inside ml-4 mt-2 space-y-2">
                <li>Direct, indirect, incidental, consequential, or punitive damages</li>
                <li>Loss of profits, revenue, data, or business opportunities</li>
                <li>Damages arising from your use or inability to use the Website</li>
                <li>Errors, omissions, or inaccuracies in Website content</li>
                <li>Unauthorized access to your data or communications</li>
              </ul>
              <p className="mt-4">
                This limitation applies whether the alleged liability is based on contract, tort, negligence, strict liability, or any other legal theory.
              </p>
              <p className="mt-4">
                <strong>Indemnification:</strong> You agree to indemnify and hold harmless Virtual Red Air PC from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from your use of the Website or violation of these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Governing Law</h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of Greece and the European Union, without regard to conflict of law principles.
              </p>
              <p className="mt-4">
                Any disputes arising from these Terms or your use of the Website shall be subject to the exclusive jurisdiction of the courts of Patras, Greece.
              </p>
              <p className="mt-4">
                If you are a consumer within the European Union, you may also have the right to bring proceedings in the courts of your country of residence.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Changes to Terms</h2>
              <p>
                We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting on this page. Your continued use of the Website after changes are posted constitutes acceptance of the modified Terms.
              </p>
              <p className="mt-4">
                We encourage you to review these Terms periodically to stay informed of any updates.
              </p>
              <p className="mt-4">
                <strong>Last Updated:</strong> January 30, 2026
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Severability</h2>
              <p>
                If any provision of these Terms is found to be invalid or unenforceable by a court of competent jurisdiction, the remaining provisions shall continue in full force and effect.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Contact Information</h2>
              <p>
                If you have any questions or concerns about these Terms of Service, please contact us:
              </p>
              <div className="mt-4 p-4 bg-red-900/20 rounded-lg border border-red-900/30">
                <p><strong>Email:</strong> <a href="mailto:info@virtualredair.com" className="text-red-400 hover:text-red-300">info@virtualredair.com</a></p>
                <p><strong>Company:</strong> Virtual Red Air PC</p>
                <p><strong>Location:</strong> Patras, Greece</p>
              </div>
            </section>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default TermsOfService;
